/**
 * Account closure — the two ways in.
 *
 * The signed-in endpoints under `/user/account` and `/vendor/account` drive the switch on the
 * settings screens. The `/account-deletion/*` pair is unauthenticated and serves the public
 * page, which the store policies require to work with no app installed and nobody signed in.
 */
import { api } from '@/lib/api';

export type DeletionStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type DeletionSubjectType = 'USER' | 'VENDOR';
export type DeletionSource = 'IN_APP' | 'PUBLIC_FORM';

/** Which set of endpoints to talk to. Mirrors the session role. */
export type DeletionRole = 'user' | 'vendor';

export type MyDeletionRequest = {
  id: string;
  source: DeletionSource;
  subject_type: DeletionSubjectType;
  status: DeletionStatus;
  reason: string | null;
  /** The admin's note. On a rejection this is the explanation, shown verbatim. */
  decision_note: string | null;
  decided_at: string | null;
  cancelled_at: string | null;
  /** When the data may be erased. Set once approved. */
  purge_after: string | null;
  purged_at: string | null;
  created_at: string;
};

export type OpenCommitments = {
  active_bookings: number;
  active_orders: number;
  net_captured_inr: string;
};

export type MyDeletionState = {
  request: MyDeletionRequest | null;
  can_request: boolean;
  /** The published grace window. Read from the API so the copy cannot drift from behaviour. */
  grace_days: number;
  open_commitments: OpenCommitments;
};

export async function fetch_my_deletion_request(role: DeletionRole): Promise<MyDeletionState> {
  const res = await api.get<MyDeletionState>(`/${role}/account/deletion-request`);
  return res.data;
}

export async function create_deletion_request(
  role: DeletionRole,
  input: { reason?: string },
): Promise<MyDeletionRequest> {
  const res = await api.post<MyDeletionRequest>(`/${role}/account/deletion-request`, {
    ...(input.reason ? { reason: input.reason } : {}),
    confirm: true,
  });
  return res.data;
}

export async function cancel_deletion_request(role: DeletionRole): Promise<MyDeletionRequest> {
  const res = await api.delete<MyDeletionRequest>(`/${role}/account/deletion-request`);
  return res.data;
}

/* ------------------------------- public page ------------------------------- */

export type DeletionPolicy = { grace_days: number; message: string };

export type PublicDeletionInput = {
  full_name: string;
  email: string;
  phone?: string;
  subject_type: DeletionSubjectType;
  reason?: string;
};

export type PublicDeletionAck = {
  reference: string;
  status: DeletionStatus;
  grace_days: number;
  message: string;
};

export async function fetch_deletion_policy(): Promise<DeletionPolicy> {
  const res = await api.get<DeletionPolicy>('/account-deletion/policy');
  return res.data;
}

export async function submit_public_deletion_request(
  input: PublicDeletionInput,
): Promise<PublicDeletionAck> {
  const res = await api.post<PublicDeletionAck>('/account-deletion/requests', {
    ...input,
    phone: input.phone || undefined,
    reason: input.reason || undefined,
    confirm: true,
  });
  return res.data;
}
